'use client'

import { Bell, CheckCheck, MoreHorizontal } from 'lucide-react'
import * as React from 'react'

import { cn } from '../cn'
import { NOTIFICATION_GENERIC_CIRCLE_CLASS } from '../notification-generic-circle'
import { Avatar, AvatarFallback, AvatarImage } from './avatar'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './dropdown-menu'
import { Skeleton } from './skeleton'

/*
 * A plain notification list page for apps outside the unified inbox: the
 * customer-facing app (apps/ordo) shows its own alerts with it. Internal apps
 * use `NotificationCenterPage` from `./notification-center` instead.
 */

export interface NotificationListActor {
  name: string | null
  image?: string | null
}

export interface NotificationListAction {
  id: string
  label: string
  destructive?: boolean
  onSelect: () => void | Promise<void>
}

export interface NotificationListItem {
  id: string
  unread: boolean
  actor?: NotificationListActor | null
  actionText: React.ReactNode
  targetText?: React.ReactNode
  timestamp: React.ReactNode
  tabIds?: string[]
  openLabel?: string
  fallbackIcon?: React.ReactNode
  actions?: NotificationListAction[]
}

export interface NotificationListTab {
  id: string
  label: string
}

export interface NotificationListPageProps {
  items: NotificationListItem[]
  unreadCount: number
  loading?: boolean
  error?: React.ReactNode
  tabs?: NotificationListTab[]
  title?: string
  emptyTitle?: string
  emptyDescription?: string
  headerActions?: React.ReactNode
  /** The header's mark-all button text. Defaults to "Mark all read". */
  markAllLabel?: string
  className?: string
  onOpenItem?: (item: NotificationListItem) => void | Promise<void>
  onMarkAllRead?: () => void | Promise<void>
  onMarkRead?: (item: NotificationListItem) => void | Promise<void>
  onMarkUnread?: (item: NotificationListItem) => void | Promise<void>
  onClear?: (item: NotificationListItem) => void | Promise<void>
}

const MARK_ALL_LABEL = 'Mark all read'

const DEFAULT_TABS: NotificationListTab[] = [
  { id: 'all', label: 'All' },
  { id: 'assigned', label: 'Assigned' },
  { id: 'mentioned', label: 'Mentioned' },
]

export function filterNotificationListItems(items: NotificationListItem[], activeTab: string): NotificationListItem[] {
  if (activeTab === 'all') {
    return items
  }
  return items.filter((item) => item.tabIds?.includes(activeTab))
}

export function NotificationListPage({
  items,
  unreadCount,
  loading = false,
  error = null,
  tabs = DEFAULT_TABS,
  title = 'Notifications',
  emptyTitle = 'No notifications',
  emptyDescription = "You'll be notified when something needs your attention.",
  headerActions,
  markAllLabel,
  className,
  onOpenItem = () => undefined,
  onMarkAllRead,
  onMarkRead,
  onMarkUnread,
  onClear,
}: NotificationListPageProps) {
  const [activeTab, setActiveTab] = React.useState(tabs[0]?.id ?? 'all')
  const filtered = filterNotificationListItems(items, activeTab)

  return (
    <div className={cn('relative flex min-h-full flex-col bg-background', className)}>
      <NotificationListHeader
        title={title}
        unreadCount={unreadCount}
        headerActions={headerActions}
        markAllLabel={markAllLabel}
        onMarkAllRead={onMarkAllRead}
      />
      <div className="flex shrink-0 items-center gap-1 border-b border-border px-4">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              'border-b-2 px-3 py-2 text-sm font-medium transition-colors',
              activeTab === tab.id
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}>
            {tab.label}
          </button>
        ))}
      </div>
      {/* The PAGE scrolls; overscroll-contain here swallowed every wheel over the list. */}
      <div className="flex-1 p-4">
        {loading ? (
          <NotificationListSkeleton />
        ) : error ? (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
            {error}
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-lg border border-border bg-card p-12 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
              <Bell className="h-6 w-6 text-muted-foreground" />
            </div>
            <h3 className="typo-heading mb-1">{emptyTitle}</h3>
            <p className="text-sm text-muted-foreground">{emptyDescription}</p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-border bg-card">
            {filtered.map((item) => (
              <NotificationListRow
                key={item.id}
                item={item}
                onOpenItem={onOpenItem}
                onMarkRead={onMarkRead}
                onMarkUnread={onMarkUnread}
                onClear={onClear}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function NotificationListHeader({
  title,
  unreadCount,
  headerActions,
  markAllLabel = MARK_ALL_LABEL,
  onMarkAllRead,
}: {
  title: string
  unreadCount: number
  headerActions?: React.ReactNode
  markAllLabel?: string
  onMarkAllRead?: () => void | Promise<void>
}) {
  const unreadLabel = unreadCount > 99 ? '99+' : unreadCount > 0 ? String(unreadCount) : null
  return (
    <div className="flex items-center justify-between border-b border-border px-4 py-4">
      <div className="flex min-w-0 items-center gap-3">
        <h1 className="typo-heading-lg">{title}</h1>
        {unreadLabel ? (
          <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-2xs font-bold text-primary-foreground">
            {unreadLabel}
          </span>
        ) : null}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {headerActions}
        {onMarkAllRead ? (
          <button
            type="button"
            disabled={unreadCount === 0}
            onClick={() => void onMarkAllRead()}
            className="flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground disabled:pointer-events-none disabled:opacity-40">
            <CheckCheck className="h-4 w-4" />
            {markAllLabel}
          </button>
        ) : null}
      </div>
    </div>
  )
}

function NotificationListRow({
  item,
  onOpenItem,
  onMarkRead,
  onMarkUnread,
  onClear,
}: {
  item: NotificationListItem
  onOpenItem: (item: NotificationListItem) => void | Promise<void>
  onMarkRead?: (item: NotificationListItem) => void | Promise<void>
  onMarkUnread?: (item: NotificationListItem) => void | Promise<void>
  onClear?: (item: NotificationListItem) => void | Promise<void>
}) {
  const menuActions = buildRowActions(item, onOpenItem, onMarkRead, onMarkUnread, onClear)
  return (
    <div
      className={cn(
        'group flex min-h-24 items-start gap-3 border-b border-border transition-colors last:border-b-0',
        item.unread ? 'bg-primary/5' : 'bg-card',
      )}>
      <button
        type="button"
        onClick={() => void onOpenItem(item)}
        className={cn(
          'flex min-w-0 flex-1 items-start gap-3 px-4 py-4 text-left transition-colors',
          item.unread ? 'hover:bg-primary/5' : 'hover:bg-muted/50',
        )}>
        <span className="pt-2">
          {item.unread ? (
            <span className="block h-2 w-2 rounded-full bg-primary" />
          ) : (
            <span className="block h-2 w-2" />
          )}
        </span>
        <NotificationActorAvatar item={item} />
        <NotificationListRowBody item={item} />
      </button>
      {menuActions.length > 0 ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="Notification actions"
              className="mr-2 mt-8 shrink-0 rounded-md p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none">
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {menuActions.map((action) => (
              <DropdownMenuItem
                key={action.id}
                className={cn(action.destructive && 'text-destructive focus:text-destructive')}
                onClick={() => void action.onSelect()}>
                {action.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </div>
  )
}

function NotificationListRowBody({ item }: { item: NotificationListItem }) {
  return (
    <div className="min-w-0 flex-1">
      <p className="text-base leading-snug">
        {item.actor?.name ? <span className="font-semibold text-foreground">{item.actor.name} </span> : null}
        <span className="text-muted-foreground">{item.actionText}</span>
      </p>
      {item.targetText ? <p className="truncate text-base font-medium text-primary">{item.targetText}</p> : null}
      <span className="text-sm text-muted-foreground">{item.timestamp}</span>
    </div>
  )
}

function NotificationActorAvatar({ item }: { item: NotificationListItem }) {
  if (!item.actor) {
    return (
      <div
        className={cn(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
          NOTIFICATION_GENERIC_CIRCLE_CLASS,
        )}>
        {item.fallbackIcon ?? <Bell className="h-4 w-4" />}
      </div>
    )
  }
  return (
    <Avatar className="h-9 w-9 shrink-0">
      {item.actor.image ? <AvatarImage src={item.actor.image} alt={item.actor.name ?? 'Notification actor'} /> : null}
      <AvatarFallback className="text-xs font-medium">{actorInitials(item.actor.name)}</AvatarFallback>
    </Avatar>
  )
}

function buildRowActions(
  item: NotificationListItem,
  onOpenItem: (item: NotificationListItem) => void | Promise<void>,
  onMarkRead?: (item: NotificationListItem) => void | Promise<void>,
  onMarkUnread?: (item: NotificationListItem) => void | Promise<void>,
  onClear?: (item: NotificationListItem) => void | Promise<void>,
): NotificationListAction[] {
  const actions: NotificationListAction[] = []
  if (item.openLabel) {
    actions.push({ id: 'open', label: item.openLabel, onSelect: () => onOpenItem(item) })
  }
  if (item.unread && onMarkRead) {
    actions.push({ id: 'mark-read', label: 'Mark as read', onSelect: () => onMarkRead(item) })
  }
  if (!item.unread && onMarkUnread) {
    actions.push({ id: 'mark-unread', label: 'Mark as unread', onSelect: () => onMarkUnread(item) })
  }
  if (onClear) {
    actions.push({ id: 'clear', label: 'Clear notification', destructive: true, onSelect: () => onClear(item) })
  }
  return [...actions, ...(item.actions ?? [])]
}

function NotificationListSkeleton() {
  return (
    <div className="space-y-2">
      {[0, 1, 2].map((index) => (
        <Skeleton key={index} className="h-20 w-full rounded-md" />
      ))}
    </div>
  )
}

function actorInitials(name: string | null): string {
  if (!name?.trim()) {
    return 'N'
  }
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')
}
